import {
  Button,
  CheckableAvatarSelectItem,
  CheckableSelectItem,
  CheckableTagSelectItem,
  DropdownSelect,
  FlexBox,
  SearchInput,
} from '@dixa/lib-shared-habla';
import {
  mappedPublicationStatuses,
  noCollectionsId,
  useArticlesFilters,
} from 'collection/pages/articles/hooks/useArticlesFilters';
import { ArticleAddModal } from 'collection/pages/collectionEdit/components/modals/components/articleAddModal';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  LanguageFieldsFragment,
  LanguageId,
  useOwnersQuery,
} from 'lib-knowledge-management-common/graphql';
import { usePaginatedCollections } from 'lib-knowledge-management-common/hooks/usePaginatedCollections';
import { useModal } from 'lib-knowledge-modal';
import { ItemType } from 'lib-knowledge-utils/components/itemType';
import { useContext } from 'react';
import { noop } from 'utils';

import { useDebouncedSearchValueInUrl } from 'hooks/useDebouncedSearchValueInUrl';

import styles from './articlesFilters.module.scss';

const statusFilterListItems: ItemType[] = [
  {
    id: mappedPublicationStatuses.NeverPublished,
    name: 'Pre-release',
    color: 'var(--dixa-typography-color-tertiary)',
  },
  {
    id: mappedPublicationStatuses.Published,
    name: 'Published',
    color: 'var(--dixa-color-green-500)',
  },
  {
    id: mappedPublicationStatuses.Unpublished,
    name: 'Unpublished',
    color: 'var(--dixa-typography-color-tertiary)',
  },
  {
    id: mappedPublicationStatuses.PublishedWithDraft,
    name: 'Work in progress',
    color: 'var(--dixa-color-green-500)',
  },
];

export function ArticlesFilters(): JSX.Element {
  const { showModal } = useModal();

  return (
    <FlexBox gap="200">
      <SearchFilter />
      <CollectionFilter />
      <StatusFilter />
      <OwnerFilter />
      <LanguageSelect />
      <Button
        onClick={() =>
          showModal(ArticleAddModal, {
            presetParent: undefined,
          })
        }
      >
        New article
      </Button>
    </FlexBox>
  );
}

export function SearchFilter(): JSX.Element {
  const { instantSearchValue, onChange } = useDebouncedSearchValueInUrl();

  return (
    <SearchInput
      initialSearch={instantSearchValue || undefined}
      onChange={onChange}
      onSubmit={noop}
    />
  );
}
export function CollectionFilter(): JSX.Element {
  const { actions, store } = useArticlesFilters();
  const { data, hasMore, fetchNextPage } = usePaginatedCollections();
  const selectedItems: string[] = [
    ...(store.collectionId || []),
    ...(store.excludeOrphans === 'false' ? [noCollectionsId] : []),
  ];

  function onChange(value: ItemType) {
    // Habla doesn't give you info on whether the item was added
    // or removed, so you need to work that out for yourself
    if (selectedItems.find((id) => id === value.id)) {
      const filteredIds = selectedItems.filter((id) => id !== value.id);
      actions.setCollections(filteredIds);
      return;
    }
    // The incoming value was not already selected, so add it
    const updatedIds = [...selectedItems, value.id.toString()];
    actions.setCollections(updatedIds);
  }

  function onClear() {
    actions.setCollections([]);
  }

  const noCollectionListItem: ItemType = {
    id: noCollectionsId,
    name: 'No collection',
    description: 'Include articles that are not in any collection',
  };
  const listItems: ItemType[] = [
    noCollectionListItem,
    ...(data?.collections.results ?? []).map((collection) => {
      const item: ItemType = {
        id: collection.id,
        name: collection.title,
      };
      return item;
    }),
  ];
  return (
    <DropdownSelect
      buttonProps={{
        label: selectedItems.length || undefined,
      }}
      itemComponent={CheckableSelectItem}
      items={listItems}
      keepOpen
      noArrow={false}
      onChange={onChange}
      onClear={onClear}
      selectedItems={selectedItems}
      pagination={{
        infinite: true,
        nextEnable: hasMore,
        previousEnable: false,
        onNext: fetchNextPage,
      }}
    >
      Collections
    </DropdownSelect>
  );
}

function StatusFilter(): JSX.Element {
  const { actions, store } = useArticlesFilters();
  const publicationStatuses = store.publicationStatus || [];

  function onChange(val: ItemType) {
    if (publicationStatuses.find((s) => s === val.id)) {
      const filteredStatuses = publicationStatuses.filter(
        (s) => s !== val.id.toString(),
      );
      actions.setPublicationStatuses(filteredStatuses);
      return;
    }
    const updatedStatuses = [...publicationStatuses, val.id.toString()];
    actions.setPublicationStatuses(updatedStatuses);
  }

  function onClear() {
    actions.setPublicationStatuses([]);
  }

  return (
    <DropdownSelect
      buttonProps={{
        label: publicationStatuses.length || undefined,
      }}
      itemComponent={CheckableTagSelectItem}
      items={statusFilterListItems}
      keepOpen
      noArrow={false}
      onChange={onChange}
      onClear={onClear}
      selectedItems={publicationStatuses}
    >
      Status
    </DropdownSelect>
  );
}

export function OwnerFilter(): JSX.Element {
  const { actions, store } = useArticlesFilters();
  const { data } = useOwnersQuery();
  const ownerIds = store.ownerId || [];

  function onChange(val: ItemType) {
    if (ownerIds.find((s) => s === val.id)) {
      const filteredOwners = ownerIds.filter((s) => s !== val.id.toString());
      actions.setOwners(filteredOwners);
      return;
    }
    const updatedOwnerIds = [...ownerIds, val.id.toString()];
    actions.setOwners(updatedOwnerIds);
  }

  function onClear() {
    actions.setOwners([]);
  }

  const owners: ItemType[] = (data?.articles.owners || []).map((owner) => {
    const item: ItemType = {
      id: owner.id,
      name: owner.name || '',
      // @ts-expect-error Habla doesn't accept avatar_url yet on ItemType but the value still gets passed through
      avatar_url: owner.avatarUrl || undefined,
    };
    return item;
  });

  return (
    <DropdownSelect
      buttonProps={{
        label: ownerIds.length || undefined,
      }}
      dropdownProps={{
        className: styles.dropdownSelectPanel,
      }}
      itemComponent={CheckableAvatarSelectItem}
      items={owners}
      keepOpen
      noArrow={false}
      onChange={onChange}
      onClear={onClear}
      selectedItems={ownerIds}
    >
      Owner
    </DropdownSelect>
  );
}

const createLanguageItems = (
  languages: LanguageFieldsFragment[],
  defaultLanguageId: LanguageId,
) =>
  languages.map((l) => ({
    id: l.languageId,
    name:
      defaultLanguageId === l.languageId
        ? `${l.description} (Default)`
        : l.description,
  }));

export function LanguageSelect(): JSX.Element {
  const { actions, store } = useArticlesFilters();
  const { enabledLanguages, defaultLanguage } = useContext(LanguageContext);

  const selectedItems =
    enabledLanguages
      ?.filter((l) => store.langId?.includes(l.languageId))
      .map(({ languageId, description }) => ({
        id: languageId,
        name: description ?? '',
      })) ?? [];

  const items = createLanguageItems(
    enabledLanguages || [],
    defaultLanguage.languageId,
  );

  return (
    <DropdownSelect
      itemComponent={CheckableSelectItem}
      buttonProps={{
        label: selectedItems.length || undefined,
      }}
      items={items}
      onChange={({ id: langId }, checked) => {
        const langIds = selectedItems.map(({ id }) => id);
        actions.setLanguageId(
          checked
            ? [...langIds, langId]
            : langIds.filter((id) => id !== langId),
        );
      }}
      selectedItems={selectedItems}
      noArrow={false}
      keepOpen
      onClear={() => actions.setLanguageId([])}
      onClearText="Default language"
    >
      Language
    </DropdownSelect>
  );
}
